import Invoice from './Invoice';
import { SupportingDocument } from './supporting-document';
import { Pet } from '@barkibu/noma-commons';

export interface SupportingDocumentInvoicesGroup {
    supportingDocument: SupportingDocument;
    invoices: Invoice[];
}

export class InvoicesDetails {
    invoices: Invoice[];
    petName: Pet['name'];

    constructor(
        invoices: Invoice[],
        private pet: Pet
    ) {
        this.invoices = invoices;
        this.petName = pet?.name;
    }

    updateInvoices(updatedInvoice: Invoice): InvoicesDetails {
        const updatedInvoices = this.invoices?.map((invoice) =>
            invoice.key != null && invoice.key === updatedInvoice.key ? updatedInvoice : invoice
        );

        return new InvoicesDetails(updatedInvoices, this.pet);
    }

    removeInvoice(invoiceKey: string): InvoicesDetails {
        const remainingInvoices = this.invoices?.filter((invoice) => invoice.key !== invoiceKey) ?? [];
        return new InvoicesDetails(remainingInvoices, this.pet);
    }

    /**
     * Groups invoices by their supporting document, preserving the order in which the documents
     * are first encountered. A document with multiple invoices appears once with all of its
     * invoices; siblings stay grouped instead of repeating in the carousel.
     */
    groupedBySupportingDocument(): SupportingDocumentInvoicesGroup[] {
        const groups: SupportingDocumentInvoicesGroup[] = [];
        const indexByKey = new Map<string, number>();
        for (const invoice of this.invoices ?? []) {
            const documentKey = invoice.supportingDocument?.key;
            if (documentKey == null) {
                groups.push({ supportingDocument: invoice.supportingDocument, invoices: [invoice] });
                continue;
            }
            const existingIndex = indexByKey.get(documentKey);
            if (existingIndex == null) {
                indexByKey.set(documentKey, groups.length);
                groups.push({ supportingDocument: invoice.supportingDocument, invoices: [invoice] });
            } else {
                groups[existingIndex].invoices.push(invoice);
            }
        }
        return groups;
    }

    get areDigitalised(): boolean {
        return this.invoices.every((invoice) => invoice.isDigitalised) || false;
    }
}
